import 'react-loading-skeleton/dist/skeleton.css';

import { memo, useMemo } from 'react';

import { getSortedAchievements } from '@didaskill/domain/users/shared';
import type { StudentAchievement } from '@didaskill/domain/users/types';
import type { AvatarShape } from '@didaskill/types/web';
import { Avatar, OrnamentWrapper } from '@didaskill/ui/design-system';

import { StarBackground } from '../star-background/StarBackground';
import * as styles from './AchievementsCard.styles';
import { AchievementsList } from './achievements-list/AchievementsList';
import { AchievementsListSkeleton } from './achievements-list/AchievementsList.skeleton';

export type AchievementsCardProps = {
  achievements: StudentAchievement[];
  user: AvatarShape;
  isLoading?: boolean;
};

export const AchievementsCardBase = ({
  achievements,
  user,
  isLoading = false,
}: AchievementsCardProps) => {
  const sortedAchievements = useMemo(() => getSortedAchievements(achievements), [achievements]);

  const firstThreeAchievements = useMemo(
    () => (sortedAchievements || []).slice(0, 6),
    [sortedAchievements],
  );

  return (
    <div css={styles.cardBody} id="achievements-card">
      <OrnamentWrapper element="sparkles" size={OrnamentWrapper.SIZES.SMALL}>
        <div css={styles.avatarWithBackgroundContainer}>
          <StarBackground />

          <div css={styles.avatarContainer}>
            <Avatar user={user} size={Avatar.SIZES.OVERSIZED} shape={Avatar.SHAPES.CIRCLE} />
          </div>
        </div>
      </OrnamentWrapper>

      <div css={styles.achievementsZone}>
        <div css={styles.achievementsList}>
          {isLoading ? (
            <AchievementsListSkeleton />
          ) : (
            <AchievementsList achievements={firstThreeAchievements} />
          )}
        </div>
      </div>
    </div>
  );
};

AchievementsCardBase.displayName = 'AchievementsCard';

export const AchievementsCard = memo(AchievementsCardBase);
